import React, { useState, useEffect, useRef, useCallback } from 'react';

// ══════════════════════════════════════════════════════════════════════════════
// A.D.M — INTELLIGENCE OVERLAY SYSTEM
// AI Consciousness stream, layer controls, autonomous zoom events
// ══════════════════════════════════════════════════════════════════════════════

// ── AI consciousness stream messages ─────────────────────────────────────────
const STREAM_LIBRARY = [
  // Scanning
  'SCANNING PACIFIC GRID SECTOR 7-ALPHA…',
  'CROSS-REFERENCING AARO DATABASE 2024-2026…',
  'TEMPORAL ANOMALY DETECTED — GRID 44-N…',
  'RECALIBRATING CONFIDENCE MATRIX…',
  'PATTERN 7A CONVERGENCE INCREASING…',
  'MILITARY FLIGHT DEVIATION LOGGED…',
  'THERMAL SIGNATURE DETECTED — LOW EARTH ORBIT…',
  'SIGINT INTERCEPT — CLASSIFICATION PENDING…',
  'SATELLITE IR SWEEP — NORTH ATLANTIC SECTOR…',
  'ANOMALY CLUSTER FORMING — WESTERN PACIFIC…',
  // Analysis
  'CROSS-LANGUAGE REPORT ANALYSIS ACTIVE…',
  'NEURAL PATHWAY RECALIBRATION…',
  'QUANTUM ENTANGLEMENT SIGNATURE — UNCLASSIFIED…',
  'GEOMAGNETIC BASELINE DEVIATION — KP +2.3…',
  'HISTORICAL PATTERN MATCH — CLASS DELTA-7…',
  'WITNESS DENSITY MAP UPDATING…',
  'ORBITAL SCAN SWEEP COMPLETE — 847 OBJECTS TRACKED…',
  'ATMOSPHERIC PRESSURE ANOMALY — COORDINATES LOCKED…',
  'EM FIELD DISTORTION — 340HZ BAND ANOMALY…',
  'NUCLEAR PROXIMITY ALERT — MONITORING ACTIVE…',
  // Prediction
  'PROBABILITY SIMULATION RUNNING — ITERATION 4471…',
  'PATTERN LIBRARY UPDATED — 23 NEW CORRELATIONS…',
  'TRAJECTORY MODELING — CONFIDENCE 91.4%…',
  'DEEP SCAN COMPLETE — 0 CONVENTIONAL EXPLANATIONS…',
  'GOVERNMENT ACTIVITY SPIKE — SECTOR CLASSIFIED…',
  'OBJECT CLASSIFICATION — NON-TERRESTRIAL PROBABLE…',
  'SENSOR FUSION COMPLETE — MULTI-DOMAIN CONFIRMED…',
  'TACTICAL ANALYSIS — INTERCEPT VECTOR COMPUTED…',
  'SIGNAL PULSE DETECTED — FREQUENCY 432.7 MHZ…',
  'REPROCESSING HISTORICAL ARCHIVE — 1947 TO PRESENT…',
  // Alerts
  'ACTIVE MONITORING — 12 CONCURRENT ANOMALIES…',
  'PREDICTIVE ENGINE CYCLE #' + Math.floor(Math.random()*9000+1000) + ' COMPLETE…',
  'CLASSIFIED SECTOR ACTIVITY — EYES ONLY…',
  'WITNESS REPORT VERIFIED — CREDIBILITY SCORE 94…',
  'RADAR CONTACT — TRANSMEDIUM OBJECT SUSPECTED…',
  'SOLAR WIND CORRELATION POSITIVE — KP INDEX 4.2…',
  'PATTERN CONVERGENCE — HISTORICAL MATCH FOUND…',
  'ORBITAL DEBRIS CHECK NEGATIVE — ANOMALY PERSISTS…',
  'CRYPTOGRAPHIC SIGNAL DETECTED — DECRYPTION PENDING…',
  'REAL-TIME SENSOR ARRAY ACTIVE — ALL NODES ONLINE…',
];

// ── Layer definitions ─────────────────────────────────────────────────────────
const LAYERS = [
  { id:'infrared',    label:'SATELLITE IR',      icon:'🔴', color:'#ff4545', desc:'Infrared satellite coverage'   },
  { id:'em',          label:'EM MAPPING',         icon:'⚡', color:'#00cfff', desc:'Electromagnetic field mapping' },
  { id:'military',    label:'MIL FLIGHT PATHS',   icon:'✈', color:'#ffaa00', desc:'Military flight deviations'    },
  { id:'atmospheric', label:'ATMOSPHERIC',         icon:'🌊', color:'#00ff9d', desc:'Atmospheric pressure systems'  },
  { id:'oceanic',     label:'OCEANIC',             icon:'🌊', color:'#0088ff', desc:'Deep ocean disturbances'       },
  { id:'historical',  label:'HISTORICAL',          icon:'📍', color:'#aa55ff', desc:'Historical event overlay'      },
  { id:'signal',      label:'SIGNAL DENSITY',      icon:'📡', color:'#ffff00', desc:'Signal density mapping'        },
  { id:'government',  label:'GOV ACTIVITY',        icon:'🏛', color:'#ff8800', desc:'Government activity tracking'  },
  { id:'deepscan',    label:'DEEP SCAN',           icon:'🔬', color:'#ff00aa', desc:'Full spectrum deep scan'        },
];

// ── AI Consciousness Stream ───────────────────────────────────────────────────
export function AIConsciousnessStream({ globalThreat, seismicCount, solarKp, majorQuakes, aiThoughts, aiFocus, aiDiscoveries }) {
  const [lines,    setLines]   = useState([]);
  const [visible,  setVisible] = useState(true);
  const timerRef = useRef(null);
  const lineCount = useRef(0);

  const addLine = useCallback(() => {
    const msg = STREAM_LIBRARY[Math.floor(Math.random()*STREAM_LIBRARY.length)];
    const ts  = new Date().toISOString().slice(11,23);
    const lineType = Math.random();
    lineCount.current++;

    setLines(prev => [
      {
        id:        lineCount.current,
        text:      msg,
        ts,
        type:      lineType > 0.9 ? 'alert' : lineType > 0.7 ? 'highlight' : 'normal',
        opacity:   1,
      },
      ...prev.slice(0, 11),
    ]);
  }, []);

  useEffect(() => {
    // Initial burst
    for(let i=0;i<6;i++) setTimeout(addLine, i*120);
    // Continuous stream
    const fast = setInterval(addLine, 1200+Math.random()*800);
    timerRef.current = fast;
    return () => clearInterval(fast);
  }, [addLine]);

  // Inject REAL AI thoughts from cognition engine — top priority
  useEffect(() => {
    if (!aiThoughts?.length) return;
    aiThoughts.forEach((thought, i) => {
      setTimeout(() => {
        setLines(prev => [{
          id:      Date.now() + i,
          ts:      new Date().toISOString().slice(11,23),
          text:    thought,
          type:    thought.includes('⚠') || thought.includes('RED') || thought.includes('ALERT') ? 'alert'
                 : thought.includes('FOCUS') || thought.includes('DETECT') ? 'highlight'
                 : 'normal',
          opacity: 1,
        }, ...prev.slice(0,11)]);
      }, i * 300);
    });
  }, [aiThoughts]);

  // Inject AI focus directive
  useEffect(() => {
    if (!aiFocus?.name) return;
    setLines(prev => [{
      id:   Date.now(),
      ts:   new Date().toISOString().slice(11,23),
      text: `AI DIRECTIVE: FOCUSING ON ${aiFocus.name?.toUpperCase()}…`,
      type: 'highlight',
      opacity: 1,
    }, ...prev.slice(0,11)]);
  }, [aiFocus]);

  // Inject AI discoveries
  useEffect(() => {
    if (!aiDiscoveries?.length) return;
    const disc = aiDiscoveries[0];
    if (disc?.description) {
      setLines(prev => [{
        id:   Date.now(),
        ts:   new Date().toISOString().slice(11,23),
        text: `CORRELATION: ${disc.description.slice(0,65)}…`,
        type: 'alert',
        opacity: 1,
      }, ...prev.slice(0,11)]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiDiscoveries?.length]);

  // Inject real seismic data
  useEffect(() => {
    if (seismicCount > 0) {
      setLines(prev => [{
        id: Date.now(), ts: new Date().toISOString().slice(11,23),
        text: `USGS REAL-TIME: ${seismicCount} SEISMIC EVENTS TRACKED LAST HOUR…`,
        type: seismicCount > 5 ? 'alert' : 'highlight',
        opacity: 1,
      }, ...prev.slice(0,11)]);
    }
  }, [seismicCount]);

  useEffect(() => {
    if(solarKp && parseFloat(solarKp) > 3) {
      setLines(prev => [{
        id: Date.now(), ts: new Date().toISOString().slice(11,23),
        text: `NOAA SPACE WEATHER: Kp-INDEX ${solarKp} — ${parseFloat(solarKp) >= 5 ? 'GEOMAGNETIC STORM ACTIVE' : 'ELEVATED ACTIVITY'}…`,
        type: parseFloat(solarKp) >= 5 ? 'alert' : 'highlight',
        opacity: 1,
      }, ...prev.slice(0,11)]);
    }
  }, [solarKp]);

  useEffect(() => {
    if(majorQuakes > 0) {
      setLines(prev => [{
        id: Date.now(), ts: new Date().toISOString().slice(11,23),
        text: `⚠ MAJOR SEISMIC EVENT M5.0+ DETECTED — CROSS-REFERENCING UAP DATABASE…`,
        type: 'alert', opacity: 1,
      }, ...prev.slice(0,11)]);
    }
  }, [majorQuakes]);

  // Vary speed based on threat level
  useEffect(() => {
    if(timerRef.current) clearInterval(timerRef.current);
    const interval = globalThreat==='RED' ? 700 : globalThreat==='AMBER' ? 1000 : 1400;
    timerRef.current = setInterval(addLine, interval+Math.random()*400);
    return () => clearInterval(timerRef.current);
  }, [globalThreat, addLine]);

  if(!visible) return (
    <button onClick={()=>setVisible(true)} style={{position:'fixed',bottom:'max(88px,calc(env(safe-area-inset-bottom)+80px))',left:12,zIndex:22,background:'rgba(0,20,10,0.85)',border:'1px solid rgba(0,200,100,0.25)',borderRadius:6,padding:'5px 10px',color:'rgba(0,200,100,0.6)',fontSize:9,fontFamily:'monospace',cursor:'pointer',letterSpacing:1,WebkitTapHighlightColor:'transparent'}}>
      STREAM ▶
    </button>
  );

  const threatColor = globalThreat==='RED'?'#ff4545':globalThreat==='AMBER'?'#ffaa00':'#1de050';

  return (
    <div style={{
      position:'fixed',
      bottom:'max(88px,calc(env(safe-area-inset-bottom)+80px))',
      left:12,
      zIndex:22,
      width:'min(240px,44vw)',
      background:'rgba(2,8,4,0.88)',
      border:`1px solid rgba(0,200,100,0.18)`,
      borderRadius:6,
      overflow:'hidden',
      backdropFilter:'blur(10px)',
      boxShadow:'0 4px 20px rgba(0,0,0,0.5)',
    }}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 8px',borderBottom:'1px solid rgba(0,200,100,0.12)',background:'rgba(0,20,10,0.6)'}}>
        <div style={{display:'flex',alignItems:'center',gap:5}}>
          <div style={{width:5,height:5,borderRadius:'50%',background:threatColor,animation:'adm-pulse 1.2s infinite'}}/>
          <span style={{fontSize:8,fontFamily:'monospace',color:'rgba(0,200,100,0.7)',letterSpacing:1.5,fontWeight:700}}>AI STREAM</span>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:6}}>
          <span style={{fontSize:7,fontFamily:'monospace',color:threatColor,letterSpacing:1,fontWeight:700}}>{globalThreat}</span>
          <button onClick={()=>setVisible(false)} style={{background:'none',border:'none',color:'rgba(0,200,100,0.3)',fontSize:11,cursor:'pointer',lineHeight:1,padding:0,WebkitTapHighlightColor:'transparent'}}>—</button>
        </div>
      </div>

      {/* Stream lines */}
      <div style={{padding:'4px 0',maxHeight:120,overflow:'hidden'}}>
        {lines.map((line,i) => (
          <div key={line.id} style={{
            padding:'1.5px 8px',
            fontSize:7.5,
            fontFamily:'monospace',
            letterSpacing:0.5,
            lineHeight:1.4,
            color: line.type==='alert'     ? '#ff4545' :
                   line.type==='highlight' ? '#ffaa00' :
                   `rgba(0,200,100,${0.9-i*0.07})`,
            whiteSpace:'nowrap',
            overflow:'hidden',
            textOverflow:'ellipsis',
            animation: i===0 ? 'adm-up 0.15s ease-out' : 'none',
            transition:'opacity 0.3s',
          }}>
            <span style={{color:'rgba(0,150,80,0.5)',marginRight:6}}>{line.ts}</span>
            {line.text}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Intelligence Layer Panel ──────────────────────────────────────────────────
export function LayerPanel({ activeLayers, onToggle, onClose }) {
  return (
    <div style={{
      position:'fixed',
      top:'max(120px,calc(env(safe-area-inset-top)+110px))',
      right:12,
      zIndex:30,
      width:'min(200px,54vw)',
      background:'rgba(3,8,5,0.95)',
      border:'1px solid rgba(29,224,80,0.18)',
      borderRadius:10,
      overflow:'hidden',
      backdropFilter:'blur(18px)',
      boxShadow:'0 8px 32px rgba(0,0,0,0.6)',
      animation:'adm-up 0.2s ease-out',
    }}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'9px 12px',borderBottom:'1px solid rgba(29,224,80,0.12)'}}>
        <span style={{fontSize:9,fontFamily:'monospace',color:'rgba(29,224,80,0.6)',letterSpacing:2,fontWeight:700}}>INTELLIGENCE LAYERS</span>
        <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(29,224,80,0.3)',fontSize:14,cursor:'pointer',lineHeight:1,WebkitTapHighlightColor:'transparent'}}>✕</button>
      </div>
      <div style={{padding:'6px 0'}}>
        {LAYERS.map(layer => {
          const active = activeLayers.includes(layer.id);
          return (
            <button key={layer.id} onClick={()=>onToggle(layer.id)} style={{
              display:'flex', alignItems:'center', gap:10,
              width:'100%', padding:'7px 12px', border:'none',
              background: active ? `${layer.color}14` : 'transparent',
              borderLeft: active ? `2px solid ${layer.color}` : '2px solid transparent',
              cursor:'pointer', WebkitTapHighlightColor:'transparent',
              transition:'all 0.15s',
            }}>
              <span style={{fontSize:11}}>{layer.icon}</span>
              <div style={{flex:1,textAlign:'left'}}>
                <div style={{fontSize:9,fontFamily:'monospace',color:active?layer.color:'rgba(150,180,160,0.5)',letterSpacing:0.8,fontWeight:700}}>{layer.label}</div>
                <div style={{fontSize:7.5,color:'rgba(100,140,115,0.4)',marginTop:1}}>{layer.desc}</div>
              </div>
              <div style={{width:7,height:7,borderRadius:'50%',background:active?layer.color:'rgba(80,100,90,0.3)',boxShadow:active?`0 0 6px ${layer.color}`:'none',flexShrink:0}}/>
            </button>
          );
        })}
      </div>
      <div style={{padding:'6px 12px',borderTop:'1px solid rgba(29,224,80,0.08)',fontSize:7.5,fontFamily:'monospace',color:'rgba(80,120,90,0.4)',textAlign:'center'}}>
        DEEPSPACE v4.0 · LAYER CONTROL
      </div>
    </div>
  );
}

// ── Autonomous zoom event system ─────────────────────────────────────────────
export function useAutonomousZoom(globeRef, sightings, globalThreat) {
  const timerRef = useRef(null);
  const lastZoom = useRef(Date.now());

  useEffect(() => {
    if(!sightings?.length) return;

    const autonomousEvent = () => {
      if(Date.now() - lastZoom.current < 30000) return; // Min 30s between events
      if(!globeRef?.current) return;

      // Pick a high-threat sighting
      const redSightings = sightings.filter(s => s.threatLevel==='RED');
      const pool = redSightings.length > 0 ? redSightings : sightings;
      const target = pool[Math.floor(Math.random()*pool.length)];

      if(!target?.lat || !target?.lng) return;

      lastZoom.current = Date.now();

      // Zoom to target
      globeRef.current.zoomTo(target.lat, target.lng, 8);

      // Return to overview after 6 seconds
      setTimeout(() => {
        if(globeRef?.current) globeRef.current.resetView();
      }, 6000);
    };

    // Random autonomous events every 45-90 seconds
    const scheduleNext = () => {
      const delay = 45000 + Math.random()*45000;
      timerRef.current = setTimeout(() => {
        autonomousEvent();
        scheduleNext();
      }, delay);
    };

    scheduleNext();
    return () => clearTimeout(timerRef.current);
  }, [sightings, globeRef, globalThreat]);
}

// ── Planetary memory overlay (canvas-based) ──────────────────────────────────
export function PlanetaryMemory({ show }) {
  if(!show) return null;
  return (
    <div style={{
      position:'absolute', inset:0, zIndex:5, pointerEvents:'none',
      background:'transparent',
    }}>
      <style>{`
        @keyframes memory-pulse {
          0%,100% { opacity:0.3; }
          50%      { opacity:0.7; }
        }
      `}</style>
    </div>
  );
}
